import {Switch} from '@/components/commons/switch/Switch'
import {Button} from '@/components/ui/button/Button'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {Grid, GridItem} from '@/components/ui/grid-atoms/GridAtoms'
import {XCloseIcon} from '@/components/ui/icon/Icon'
import {InputHelpText} from '@/components/ui/input-help-text/InputHelpText'
import {InputStatusIcon} from '@/components/ui/input-status-icon/InputStatusIcon'
import {Label} from '@/components/ui/label/Label'
import {Text} from '@/components/ui/text/Text'
import {AgendaEventProjectInfo} from '@/features/agenda/components/agenda-event-project-info/AgendaEventProjectInfo'
import {AgendaEventFieldsSchema} from '@/features/agenda/formSchemas'
import {isExternalStructureConsultant} from '@/features/agenda/utils'
import {useExternalStructureUserIds} from '@/features/users/hooks/useExternalStructureUserIds'
import {InputSelectPatient} from '@/features/patients/components/input-select-patient/InputSelectPatient'
import {PatientsMultiSelect} from '@/features/patients/components/patients-multi-select/PatientsMultiSelect'
import {PatientSmall} from '@/features/patients/types'
import {InputSelectProjectByPatient} from '@/features/projects/components/input-select-project-by-patient/InputSelectProjectByPatient'
import {useTranslation} from '@/translations/i18n'
import {useCallback, useEffect, useState} from 'react'
import {useController, useFieldArray, useFormContext, useWatch} from 'react-hook-form'
import {z} from 'zod'

const AgendaEventPatientsFieldsSchema = AgendaEventFieldsSchema.pick({
    category: true,
    patients: true,
    consultants: true,
    date: true,
    isGroupTreatment: true
})
type AgendaEventPatientsFieldsSchema = z.infer<typeof AgendaEventPatientsFieldsSchema>

export const AgendaEventPatientsFields = ({
    areDisabledPatientRelatedFields,
    mode
}: {
    areDisabledPatientRelatedFields: boolean
    mode?: 'create' | 'edit'
}) => {
    const {t} = useTranslation()
    const {control, setValue} = useFormContext<AgendaEventPatientsFieldsSchema>()
    const externalStructureUserIds = useExternalStructureUserIds()
    const watchedPatients = useWatch({control, name: 'patients'})
    const consultants = useWatch({control, name: 'consultants'})
    const hasExternalStructureConsultant = !!consultants?.some(consultant =>
        isExternalStructureConsultant(consultant, externalStructureUserIds)
    )
    const {
        fieldState: {error: patientsError}
    } = useController({control, name: 'patients'})
    const {field: isTreatmentGroupField} = useController({control, name: 'isGroupTreatment'})
    const category = useWatch({control, name: 'category'})

    const {fields, replace, remove} = useFieldArray({
        control,
        name: 'patients',
        keyName: 'key'
    })

    const [isPatientProjectSuspended, setIsPatientProjectSuspended] = useState(false)

    const handleUpdatePatient = useCallback(
        (
            patients: Pick<
                PatientSmall,
                'id' | 'fullName' | 'treatmentRequestId' | 'project' | 'neuromuscularDiagnosisId'
            >[]
        ) => {
            setIsPatientProjectSuspended(patients.some(patient => !!patient.project?.isSuspended))
            replace(
                patients.map(patient => ({
                    ...patient,
                    project: patient.project && !patient.project.isSuspended ? patient.project : null
                }))
            )
        },
        [replace]
    )

    // An external structure event is never tied to a PRI: drop any project already picked, the field is
    // replaced by a read-only note below
    useEffect(() => {
        if (!hasExternalStructureConsultant || !watchedPatients?.some(patient => patient.project)) {
            return
        }
        replace(watchedPatients.map(patient => ({...patient, project: null})))
    }, [hasExternalStructureConsultant, watchedPatients, replace])

    const date = useWatch({control, name: 'date.date'})

    return (
        <>
            {!areDisabledPatientRelatedFields && category == 'treatments' && (
                <Switch
                    label={t('agenda:group_treatment')}
                    size="sm"
                    {...isTreatmentGroupField}
                    checked={!!isTreatmentGroupField.value}
                    disabled={mode == 'edit'}
                    onChange={value => {
                        isTreatmentGroupField.onChange(value)
                        setValue('consultants', [{id: '', fullName: ''}])
                        if (!value) {
                            handleUpdatePatient([])
                        }
                    }}
                />
            )}
            <Flexbox direction="column" gap={4} fullWidth>
                <Grid columns={2} gap={4} fullWidth>
                    <GridItem direction="column" gap={1.5}>
                        <Label>{isTreatmentGroupField.value ? t('commons:patients') : t('commons:patient')}</Label>
                        {isTreatmentGroupField.value ? (
                            <PatientsMultiSelect
                                placeholder={t('agenda:add_patient_to_group')}
                                values={fields}
                                onChange={handleUpdatePatient}
                                errorMessage={patientsError?.message}
                            />
                        ) : (
                            <InputSelectPatient
                                placeholder={t('commons:select_patient')}
                                value={fields[0]}
                                hideBadge
                                errorMessage={patientsError?.message}
                                onChange={patient => {
                                    patient && handleUpdatePatient([patient])
                                }}
                            />
                        )}
                    </GridItem>

                    {!isTreatmentGroupField.value && fields.length > 0 && (
                        <GridItem>
                            {hasExternalStructureConsultant ? (
                                <Flexbox direction="column" gap={1.5} fullWidth>
                                    <Label>{t('commons:projects', {count: 1})}</Label>
                                    <Text palette="neutral" shade="600" size="textSm">
                                        {t('agenda:external_structure_no_project')}
                                    </Text>
                                </Flexbox>
                            ) : fields[0].project ? (
                                <InputSelectProjectByPatient
                                    patientId={fields[0]?.id}
                                    label={t('commons:projects', {count: 1})}
                                    placeholder={t('projects:search_project')}
                                    date={date}
                                    onChange={project => {
                                        handleUpdatePatient(fields.map(patient => ({...patient, project})))
                                    }}
                                    value={
                                        fields[0]?.project
                                            ? {
                                                  id: fields[0].project.id,
                                                  code: fields[0].project.code,
                                                  startDate: fields[0].project.startDate,
                                                  expirationDate: fields[0].project.expirationDate
                                              }
                                            : null
                                    }
                                    disableClosedProjects
                                />
                            ) : isPatientProjectSuspended ? (
                                <Flexbox direction="column" gap={1.5} fullWidth>
                                    <Label>{t('commons:projects', {count: 1})}</Label>
                                    <Flexbox align="center" gap={1.5}>
                                        <InputStatusIcon hasError />
                                        <InputHelpText error={t('agenda:patient_project_suspended')} />
                                    </Flexbox>
                                </Flexbox>
                            ) : null}
                        </GridItem>
                    )}
                </Grid>

                {isTreatmentGroupField.value && fields.length > 0 && (
                    <Grid columns={2} gap={4} fullWidth>
                        {fields.map((item, index) => {
                            return <AgendaEventPatientItem key={item.id} value={item} remove={() => remove(index)} />
                        })}
                    </Grid>
                )}
            </Flexbox>
        </>
    )
}

export const AgendaEventPatientItem = ({
    value,
    remove
}: {
    value: Pick<PatientSmall, 'fullName' | 'project' | 'treatmentRequestId'>
    remove: () => void
}) => {
    return (
        <GridItem gap={1.5} fullWidth>
            <Flexbox direction="column" fullWidth gap={1}>
                <Text size="textSm" weight="500" palette="neutral" shade="500">
                    {value.fullName}
                </Text>
                <AgendaEventProjectInfo showProjectLabel patientName={value.fullName} project={value.project} />
            </Flexbox>
            <Button shape="square" onClick={remove} size="sm" variant="ghost">
                <XCloseIcon />
            </Button>
        </GridItem>
    )
}
